import {
	faArrowRightFromBracket,
	faArrowRightToBracket,
	faBuilding,
	faChevronDown,
	faClipboardList,
	faFileInvoiceDollar,
	faLocationDot,
	faMoon,
	faSunBright,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalClientDto } from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../../hooks/useDisplaySettings';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import { clientDisplayId } from '../../lib/clientDisplayId';
import { useCartStore } from '../../stores/cartStore';
import { useLoginModalStore } from '../../stores/loginModalStore';

/**
 * Format a client's address into a single display line.
 * Handles missing fields gracefully — only includes parts that exist.
 */
function formatClientAddress(client: PortalClientDto): string | null {
	const parts: Array<string> = [];

	if (client.address1 != null && client.address1.length > 0) {
		parts.push(client.address1);
	}
	if (client.address2 != null && client.address2.length > 0) {
		parts.push(client.address2);
	}

	const cityStateZip: Array<string> = [];
	if (client.city != null && client.city.length > 0) {
		cityStateZip.push(client.city);
	}
	if (client.state != null && client.state.length > 0) {
		cityStateZip.push(client.state);
	}

	let locationPart = cityStateZip.join(', ');
	if (client.zip != null && client.zip.length > 0) {
		locationPart =
			locationPart.length > 0 ? `${locationPart} ${client.zip}` : client.zip;
	}

	if (locationPart.length > 0) {
		parts.push(locationPart);
	}

	return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * User menu dropdown for the portal header.
 *
 * Authenticated: shows user initials avatar with a dropdown containing
 * current location (with address), change location button (if multi-client),
 * account details, and sign out.
 *
 * Not authenticated: shows a "Sign In" button that opens the login modal.
 */
export function UserMenu(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isAuthenticated, profile, activeClient, availableClients, logout } =
		usePortalAuth();
	const display = useDisplaySettings();
	const { isDark, toggleTheme } = useTheme();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const setCart = useCartStore((s) => s.setCart);
	const [isOpen, setIsOpen] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	// Close on any pointer-down outside the menu — including taps on the
	// BottomNav (cart, etc.) or the page chrome. We can't rely on a fixed
	// inset-0 backdrop because the parent <header> is `sticky z-30` and
	// creates its own stacking context: a backdrop nested inside it can't
	// visually overlay the BottomNav (also z-30 but later in the DOM, so
	// higher in the natural paint order). A document-level mousedown
	// listener escapes the stacking context entirely. Mousedown fires
	// before click, so the underlying nav target's click handler still
	// runs after we close.
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			if (
				containerRef.current != null &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [isOpen]);

	const handleLogout = useCallback(() => {
		setIsOpen(false);
		logout();
		// Clear the cart store explicitly — the auth store's `reset()`
		// drops the auth/profile state but doesn't touch the cart, and
		// we don't want the previous user's cart contents to flash on
		// screen if they remain logged out and start anonymous browsing.
		setCart(null);
		// SPA navigate to root rather than hard-reloading. The previous
		// `window.location.href = '/'` was hitting a 404 on some
		// surfaces (notably iOS Capacitor, where the in-app origin
		// resolves the absolute path differently than the browser
		// expects). React Router's navigate keeps us inside the SPA.
		navigate('/');
		// Pop the login modal immediately. The user just hit "Sign Out"
		// — their next intention is almost always to sign back in (as
		// themselves on a different account, or as a different client),
		// so going straight to the form skips a manual click. Matches
		// the user's expectation of "logout returns me to the login form".
		openLoginModal();
	}, [logout, setCart, navigate, openLoginModal]);

	// Not authenticated — show sign in button that opens modal. Header is
	// painted in the brand's primary color so trigger text/icons sit on a
	// dark surface; use white-on-brand here, not gray-on-white.
	if (!isAuthenticated || profile == null) {
		return (
			<button
				type="button"
				onClick={() => openLoginModal()}
				className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-brand-primary-fg hover:bg-brand-primary-fg/10 transition-colors min-h-touch"
			>
				<FontAwesomeIcon icon={faArrowRightToBracket} />
				<span className="hidden sm:inline">{t('Sign In')}</span>
			</button>
		);
	}

	// Build initials for avatar
	const initials =
		`${profile.firstName?.charAt(0) ?? ''}${profile.lastName?.charAt(0) ?? ''}`.toUpperCase() ||
		'?';

	const activeAddress =
		activeClient != null ? formatClientAddress(activeClient) : null;
	const hasMultipleClients = availableClients.length > 1;

	return (
		<div className="relative" ref={containerRef}>
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-brand-primary-fg/10 transition-colors min-h-touch"
				aria-label={t('User menu')}
			>
				{/* Avatar circle with initials. Avatar bg is the brand accent
				    (its own surface) so its `text-white` is independent of
				    the header's foreground color and stays anchored. */}
				<div className="w-8 h-8 rounded-full bg-portal-accent text-white flex items-center justify-center text-xs font-semibold shrink-0">
					{initials}
				</div>
				{/* Name + client preview sit on the brand header — read color
				    via `text-brand-primary-fg` so they remain readable
				    whether the header is dark or light (BrandThemeApplier
				    flips it based on the active primary's luminance). */}
				<div className="hidden md:flex flex-col items-start text-left max-w-[160px]">
					<span className="text-sm font-medium text-brand-primary-fg truncate w-full">
						{t(profile.firstName ?? '')} {t(profile.lastName ?? '')}
					</span>
					{activeClient != null && (
						<span className="text-xs text-brand-primary-fg/75 truncate w-full">
							{t(activeClient.name ?? '')} · {t(clientDisplayId(activeClient))}
						</span>
					)}
				</div>
				<FontAwesomeIcon
					icon={faChevronDown}
					className="text-xs text-brand-primary-fg/70 hidden md:block"
				/>
			</button>

			{isOpen && (
				<>
					{/* Dropdown menu — outside-click handled by the document
					    mousedown listener above (a fixed inset-0 backdrop
					    can't escape the parent header's z-30 stacking
					    context, so taps on the BottomNav at z-30 wouldn't
					    dismiss it). */}
					<div className="absolute right-0 top-full mt-1 z-50 w-80 bg-brand-surface border border-brand-border rounded-lg shadow-lg overflow-hidden">
						{/* User identity section */}
						<div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
							<div className="text-sm font-medium text-gray-900 dark:text-gray-100">
								{t(profile.firstName ?? '')} {t(profile.lastName ?? '')}
							</div>
							<div className="text-xs text-brand-muted mt-0.5">
								{t(profile.email ?? '')}
							</div>
						</div>

						{/* Current location — always shown if a client is active */}
						{activeClient != null && (
							<div className="border-b border-gray-100 dark:border-gray-700">
								<div className="px-4 py-2 text-xs font-semibold text-brand-muted uppercase tracking-wider">
									{t('Location')}
								</div>
								<div className="px-4 pb-3">
									<div className="flex items-start gap-2">
										<FontAwesomeIcon
											icon={faBuilding}
											className="text-xs w-4 text-center text-gray-400 shrink-0 mt-0.5"
										/>
										<div className="flex-1 min-w-0">
											<div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
												{t(activeClient.name ?? '')}
											</div>
											<div className="text-xs text-brand-muted mt-0.5">
												{t('ID')}: {t(clientDisplayId(activeClient))}
											</div>
											{activeAddress != null && (
												<div className="text-xs text-brand-muted mt-0.5">
													{t(activeAddress)}
												</div>
											)}
										</div>
									</div>
									{/* Change Location button — only if multi-client */}
									{hasMultipleClients && (
										<button
											type="button"
											onClick={() => {
												setIsOpen(false);
												navigate('/select-client');
											}}
											className="mt-2 flex items-center gap-1.5 text-xs text-portal-accent hover:underline"
										>
											<FontAwesomeIcon
												icon={faLocationDot}
												className="text-[10px]"
											/>
											<span>{t('Change Location')}</span>
										</button>
									)}
								</div>
							</div>
						)}

						{/* Menu actions */}
						<div className="py-1">
							{/* PD-14239: Orders gated on order-history visibility. */}
							{display.canViewOrderHistory && (
								<button
									type="button"
									onClick={() => {
										setIsOpen(false);
										navigate('/orders');
									}}
									className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input flex items-center gap-2 min-h-touch"
								>
									<FontAwesomeIcon
										icon={faClipboardList}
										className="text-xs w-4 text-center"
									/>
									<span>{t('Orders')}</span>
								</button>
							)}

							{/* PD-14239: Invoices hidden when the hide-pricing flag is on
							    (getMe forces canViewInvoices=false), so a hide-pricing
							    buyer never sees a link into the financial section. */}
							{display.canViewInvoices && (
								<button
									type="button"
									onClick={() => {
										setIsOpen(false);
										navigate('/invoices');
									}}
									className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input flex items-center gap-2 min-h-touch"
								>
									<FontAwesomeIcon
										icon={faFileInvoiceDollar}
										className="text-xs w-4 text-center"
									/>
									<span>{t('Invoices')}</span>
								</button>
							)}

							<button
								type="button"
								onClick={() => {
									setIsOpen(false);
									navigate('/account');
								}}
								className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input flex items-center gap-2 min-h-touch"
							>
								<FontAwesomeIcon
									icon={faUser}
									className="text-xs w-4 text-center"
								/>
								<span>{t('Account Details')}</span>
							</button>

							<button
								type="button"
								onClick={toggleTheme}
								className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input flex items-center gap-2 min-h-touch"
							>
								<FontAwesomeIcon
									icon={isDark ? faSunBright : faMoon}
									className="text-xs w-4 text-center"
								/>
								<span>{isDark ? t('Light Mode') : t('Dark Mode')}</span>
							</button>

							<button
								type="button"
								onClick={handleLogout}
								className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-brand-input flex items-center gap-2 min-h-touch"
							>
								<FontAwesomeIcon
									icon={faArrowRightFromBracket}
									className="text-xs w-4 text-center"
								/>
								<span>{t('Sign Out')}</span>
							</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
}
